import React, { useMemo } from 'react';
import { MatchListItemLayout } from '../../utils/match-list.constants';
import { MatchListCommon, FilteredMarketGroupsOverview } from '../../utils/match-list-models';
import { MarketGroupOverviewModel, DisciplinesType } from '@om-fe/models';
import { PlatformTypes } from '@om-fe/utils';
import { GetMarketGroupsResponse, GetMarketGroupsOptions } from '@om-fe/api';
import { RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks';
import keyBy from 'lodash/keyBy';
import LiveMatchListSelectorGroup from './live-match-list-selector-group';
import { Spinner } from '@om-fe/widgets';

export interface LiveMatchListPublicProps extends MatchListCommon {
    readonly disciplineId: string;
    readonly locationId?: string;
    readonly isDefault?: boolean;
    readonly isLoadingData?: boolean;
    readonly eventCategoryId?: string;
    readonly itemLayout?: MatchListItemLayout;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly marketGroupsOverviewNumber?: number;
    readonly hasMarketSelector: boolean;
    readonly deviceType?: PlatformTypes;
    readonly showMarketSelectorHeader?: boolean;
    readonly isAuthenForTopic?: boolean;
}

type LiveMatchListSelectorProps = LiveMatchListPublicProps;

function LiveMatchListSelector(
    {
        instanceId,
        disciplineId,
        itemLayout,
        liveStatus,
        locationId,
        eventCategoryId,
        limitNumberOfEvents,
        mainMarketsLimit,
        marketsHeaderOptions,
        hasMarketSelector,
        showMatchListHeader,
        showMarketInMatchListGroupHeader,
        deviceType,
        activeMatchId,
        showMarketSelectorHeader,
        isAuthenForTopic,
    }: LiveMatchListSelectorProps,
) {
    const params: GetMarketGroupsOptions = useMemo(() => ({
        sportId: disciplineId === DisciplinesType.ElectronicSports && locationId ? locationId : disciplineId,
        liveStatus,
    }), [disciplineId, liveStatus, locationId]);
    const [rpcResponse] = useRpcState<GetMarketGroupsResponse>('marketGroupsOverview', params);

    if (
        rpcResponse.status === RpcStateStatus.Loading ||
        rpcResponse.status === RpcStateStatus.Initial ||
        rpcResponse.status === RpcStateStatus.Failed
    ) {
        return <Spinner />;
    }

    const { data } = rpcResponse;
    const marketGroupsOverviewData: MarketGroupOverviewModel[] | undefined = data.response;
    const filteredMarketGroupsOverviewData: Record<string, FilteredMarketGroupsOverview> = keyBy(marketGroupsOverviewData, 'id');
    const filteredMarketGroupsOverviewListData = marketGroupsOverviewData?.map(item => item.id);
    const groupOverviewData = filteredMarketGroupsOverviewListData?.slice(0, mainMarketsLimit).join(',');

    return (
        <>
            <LiveMatchListSelectorGroup 
                itemLayout={itemLayout}
                disciplineId={disciplineId}
                instanceId={instanceId}
                limitNumberOfEvents={limitNumberOfEvents}
                liveStatus={liveStatus}
                mainMarketsLimit={mainMarketsLimit}
                marketsHeaderOptions={marketsHeaderOptions}
                eventCategoryId={eventCategoryId}
                locationId={locationId}
                groupOverviewData={groupOverviewData}
                hasMarketSelector={hasMarketSelector}
                showMatchListHeader={showMatchListHeader}
                showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
                activeMatchId={activeMatchId}
                filteredMarketGroupsOverview={filteredMarketGroupsOverviewData}
                filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewListData}
                deviceType={deviceType}
                isAuthenForTopic={isAuthenForTopic}
                showMarketSelectorHeader={showMarketSelectorHeader}
            />
        </>
    );
}

export default LiveMatchListSelector;
